import { Button, Checkbox, Drawer, Radio, RadioGroup, TextArea } from "@heroui/react"
import { tamu } from "@ramoo/i18n/tamu"
import { labelHarga, rupiah } from "@ramoo/self-order-ui"
import { type ReactNode, useMemo, useState } from "react"
import { Jumlah } from "./bagian"
import {
  batasJumlah,
  type GrupModifier,
  grupProduk,
  grupValid,
  hargaSatuan,
  jumlahTerpilih,
  type PilihanItem,
  type Produk,
  pilihanLengkap,
} from "./katalog"

const teks = tamu()

const petunjukGrup = (g: GrupModifier): string => {
  if (g.minSelect > 0)
    return g.maxSelect > 1 ? teks.wajibRentang(g.minSelect, g.maxSelect) : teks.wajib
  return g.maxSelect > 0 && g.maxSelect !== 1 ? teks.opsionalMaks(g.maxSelect) : teks.opsional
}

/**
 * Satu kelompok pilihan: judul di kiri, petunjuk wajib/opsional di kanan, lalu pilihannya.
 * `kurang`: Tambah sudah ditekan tapi grup wajib ini belum terisi — petunjuknya jadi merah.
 */
function Grup({
  id,
  judul,
  petunjuk,
  kurang = false,
  children,
}: {
  id: string
  judul: string
  petunjuk: string
  kurang?: boolean
  children: ReactNode
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-2">
        <b id={id} className="text-sm font-semibold text-foreground">
          {judul}
        </b>
        <span
          className={kurang ? "text-xs font-semibold text-danger" : "text-xs text-muted"}
          role={kurang ? "alert" : undefined}
        >
          {kurang ? `${teks.pilihDulu} · ${petunjuk}` : petunjuk}
        </span>
      </div>
      {children}
    </div>
  )
}

/** Isi satu pilihan: nama mengisi sisa lebar, selisih harga rata kanan. */
const IsiOpsi = ({ nama, harga }: { nama: string; harga?: string | null }) => (
  <>
    <span className="min-w-0 flex-1">{nama}</span>
    {harga ? <span className="text-sm text-muted tabular-nums">{harga}</span> : null}
  </>
)

/*
 * Baris pilihan selebar lembar dan setinggi sasaran sentuh, bukan selebar dan setinggi teksnya.
 * `mt-0`: RadioGroup memberi jarak antar-radio lewat margin; di sini jaraknya sudah datang dari
 * tinggi baris, dan baris centang di grup sebelahnya harus sama rapatnya.
 */
const KELAS_OPSI = "mt-0 w-full"
const KELAS_ISI_OPSI = "min-h-11 w-full"
/* Radio bulat (keputusan Modul 12): pilih-satu dibedakan dari centang pilih-banyak yang kotak. */
const KELAS_RADIO = "rounded-full"

/**
 * Lembar detail produk: pilih varian (wajib bila ada — TIDAK otomatis varian pertama), modifier
 * dengan batas minSelect/maxSelect grup, jumlah (dibatasi `maxPerOrder`), dan catatan. Harga berjalan
 * ditampilkan; harga final tetap dihitung server.
 *
 * Mockup Modul 12: foto dan harga di kepala, radio bulat, jumlah dan "Tambah · Rp…" sebaris.
 * Tombol Tambah selalu aktif — dulu abu-abu "Lengkapi pilihan" tanpa menyebut apa yang kurang;
 * sekarang grup wajib yang belum terisi ditandai merah saat ditekan.
 *
 * Drawer HeroUI dari bawah (padanan `sheet` Pro dengan paket gratis). Esc, ketuk latar, tombol
 * tutup, dan seret ke bawah menutupnya. Pemanggil memberi `key` baru tiap kali membuka, jadi
 * pilihan selalu mulai kosong.
 */
export function DetailProduk({
  produk,
  semuaGrup,
  buka,
  onTutup,
  onTambah,
}: {
  produk: Produk
  semuaGrup: GrupModifier[]
  buka: boolean
  onTutup: () => void
  onTambah: (pilihan: PilihanItem) => void
}) {
  const grup = useMemo(() => grupProduk(produk, semuaGrup), [produk, semuaGrup])
  const [variantId, setVariantId] = useState<string | null>(null)
  const [modifierIds, setModifierIds] = useState<string[]>([])
  const [jumlah, setJumlah] = useState(1)
  const [catatan, setCatatan] = useState("")
  const [dicoba, setDicoba] = useState(false)

  const pilihan: PilihanItem = { variantId, modifierIds, jumlah, notes: catatan.trim() || null }
  const lengkap = pilihanLengkap(produk, grup, pilihan)
  const satuan = hargaSatuan(produk, grup, pilihan)
  const maks = batasJumlah(produk)

  const toggleModifier = (g: GrupModifier, id: string) => {
    setModifierIds((cur) => {
      const diGrup = cur.filter((x) => g.modifiers.some((m) => m.id === x))
      const lain = cur.filter((x) => !g.modifiers.some((m) => m.id === x))
      const terpilih = diGrup.includes(id)
      if (g.maxSelect === 1) {
        if (terpilih) return g.minSelect === 0 ? lain : cur
        return [...lain, id]
      }
      if (terpilih) return [...lain, ...diGrup.filter((x) => x !== id)]
      if (g.maxSelect > 0 && diGrup.length >= g.maxSelect) return cur
      return [...lain, ...diGrup, id]
    })
  }

  const hargaModifier = (delta: number) =>
    delta ? `${delta > 0 ? "+" : "−"}${rupiah(Math.abs(delta))}` : null

  return (
    <Drawer.Backdrop
      isOpen={buka}
      onOpenChange={(terbuka) => {
        if (!terbuka) onTutup()
      }}
    >
      <Drawer.Content placement="bottom">
        <Drawer.Dialog aria-label={teks.detailAria(produk.name)} className="mx-auto max-w-xl">
          <Drawer.Handle />
          <Drawer.CloseTrigger aria-label={teks.tutup} />
          <Drawer.Header className="flex-col items-stretch gap-3">
            <Foto url={produk.imageUrl} />
            <div className="flex flex-col gap-0.5">
              <Drawer.Heading>{produk.name}</Drawer.Heading>
              <span className="text-sm text-muted">
                {labelHarga(
                  produk.price,
                  produk.variants.map((v) => v.price),
                )}
              </span>
            </div>
          </Drawer.Header>
          <Drawer.Body className="flex flex-col gap-4">
            {produk.description ? <p className="text-sm text-muted">{produk.description}</p> : null}

            {produk.variants.length ? (
              <Grup
                id="grup-varian"
                judul={teks.varian}
                petunjuk={teks.wajib}
                kurang={dicoba && !variantId}
              >
                <RadioGroup aria-labelledby="grup-varian" value={variantId} onChange={setVariantId}>
                  {produk.variants.map((v) => (
                    <Radio key={v.id} value={v.id} className={KELAS_OPSI}>
                      <Radio.Content className={KELAS_ISI_OPSI}>
                        <Radio.Control className={KELAS_RADIO}>
                          <Radio.Indicator className={KELAS_RADIO} />
                        </Radio.Control>
                        <IsiOpsi nama={v.name} harga={rupiah(v.price)} />
                      </Radio.Content>
                    </Radio>
                  ))}
                </RadioGroup>
              </Grup>
            ) : null}

            {grup.map((g) => {
              const n = jumlahTerpilih(g, modifierIds)
              const penuh = g.maxSelect > 0 && g.maxSelect !== 1 && n >= g.maxSelect
              const idJudul = `grup-${g.id}`
              const radio = g.maxSelect === 1
              return (
                <Grup
                  key={g.id}
                  id={idJudul}
                  judul={g.name}
                  petunjuk={petunjukGrup(g)}
                  kurang={dicoba && !grupValid(g, modifierIds)}
                >
                  {radio ? (
                    <RadioGroup
                      aria-labelledby={idJudul}
                      value={g.modifiers.find((m) => modifierIds.includes(m.id))?.id ?? null}
                      onChange={(id) => toggleModifier(g, id)}
                    >
                      {g.modifiers.map((m) => (
                        <Radio key={m.id} value={m.id} className={KELAS_OPSI}>
                          <Radio.Content className={KELAS_ISI_OPSI}>
                            <Radio.Control>
                              <Radio.Indicator />
                            </Radio.Control>
                            <IsiOpsi nama={m.name} harga={hargaModifier(m.priceDelta)} />
                          </Radio.Content>
                        </Radio>
                      ))}
                    </RadioGroup>
                  ) : (
                    // biome-ignore lint/a11y/useSemanticElements: kumpulan centang berjudul; fieldset membawa gaya bawaan yang tidak dipakai di sini
                    <div role="group" aria-labelledby={idJudul} className="flex flex-col">
                      {g.modifiers.map((m) => {
                        const dipilih = modifierIds.includes(m.id)
                        return (
                          <Checkbox
                            key={m.id}
                            className={KELAS_OPSI}
                            isSelected={dipilih}
                            isDisabled={!dipilih && penuh}
                            onChange={() => toggleModifier(g, m.id)}
                          >
                            <Checkbox.Content className={KELAS_ISI_OPSI}>
                              <Checkbox.Control>
                                <Checkbox.Indicator />
                              </Checkbox.Control>
                              <IsiOpsi nama={m.name} harga={hargaModifier(m.priceDelta)} />
                            </Checkbox.Content>
                          </Checkbox>
                        )
                      })}
                    </div>
                  )}
                </Grup>
              )
            })}

            <Grup id="grup-catatan" judul={teks.catatan} petunjuk={teks.opsional}>
              <TextArea
                fullWidth
                aria-labelledby="grup-catatan"
                rows={2}
                maxLength={200}
                value={catatan}
                placeholder={teks.catatanContoh}
                onChange={(e) => setCatatan(e.target.value)}
              />
            </Grup>
          </Drawer.Body>

          <Drawer.Footer className="flex-col items-stretch gap-2">
            <div className="flex items-center gap-3">
              <Jumlah
                besar
                nilai={jumlah}
                bisaKurang={jumlah > 1}
                bisaTambah={jumlah < maks}
                onKurang={() => setJumlah((j) => Math.max(1, j - 1))}
                onTambah={() => setJumlah((j) => Math.min(maks, j + 1))}
              />
              <Button
                className="flex-1"
                size="lg"
                onPress={() => {
                  if (!lengkap) {
                    setDicoba(true)
                    return
                  }
                  onTambah({ ...pilihan, jumlah })
                  onTutup()
                }}
              >
                {teks.tambahTotal(rupiah(satuan * jumlah))}
              </Button>
            </div>
            {Number.isFinite(maks) ? (
              <p className="text-center text-xs text-muted">{teks.maksPesanan(maks)}</p>
            ) : null}
          </Drawer.Footer>
        </Drawer.Dialog>
      </Drawer.Content>
    </Drawer.Backdrop>
  )
}

/** Foto produk di kepala lembar, atau bidang aksen lembut berikon kalau tak berfoto / gagal. */
function Foto({ url }: { url: string | null }) {
  const [gagal, setGagal] = useState(false)
  if (url && !gagal)
    return (
      <img
        className="block h-33 w-full rounded-xl object-cover"
        src={url}
        alt=""
        onError={() => setGagal(true)}
      />
    )
  return (
    <span
      aria-hidden="true"
      className="grid h-33 w-full place-items-center rounded-xl bg-accent-soft text-accent-soft-foreground"
    >
      <svg
        viewBox="0 0 24 24"
        width="30"
        height="30"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <title>Tanpa gambar</title>
        <rect x="3" y="4" width="18" height="16" rx="2" />
        <circle cx="8.5" cy="9.5" r="1.5" />
        <path d="m21 16-5-5L5 20" />
      </svg>
    </span>
  )
}
